-- | Browser boundary for route reads, navigation listeners, and history writes.
module App.Browser
    ( listenBrowserNavigation
    , readLocation
    , pushUrl
    , replaceUrl
    , pushRoute
    , replaceRoute
    ) where

import Prelude

import App.Route (Route, routeUrl)
import Data.Foldable (for_)
import Data.Maybe (Maybe(..))
import Effect (Effect)
import Effect.Ref as Ref
import Foreign (unsafeToForeign)
import Routing.PushState (LocationState, PushStateInterface)
import Web.Event.EventTarget (addEventListener, eventListener, removeEventListener)
import Web.HTML (window)
import Web.HTML.Event.HashChangeEvent.EventTypes as HashEvent
import Web.HTML.Event.PopStateEvent.EventTypes as PopStateEvent
import Web.HTML.History as History
import Web.HTML.Location as Location
import Web.HTML.Window as Window

listenBrowserNavigation :: (LocationState -> Effect Unit) -> Effect (Effect Unit)
listenBrowserNavigation callback = do
    activeRef <- Ref.new true
    listenerRef <- Ref.new Nothing
    target <- Window.toEventTarget <$> window

    let cleanup = do
            active <- Ref.read activeRef
            when active do
                Ref.write false activeRef
                mbListener <- Ref.read listenerRef
                for_ mbListener \listener -> do
                    removeEventListener PopStateEvent.popstate listener false target
                    removeEventListener HashEvent.hashchange listener false target

    listener <- eventListener \_ -> do
        active <- Ref.read activeRef
        when active do
            cleanup
            callback =<< currentLocationState

    Ref.write (Just listener) listenerRef
    addEventListener PopStateEvent.popstate listener false target
    addEventListener HashEvent.hashchange listener false target
    pure cleanup

currentLocationState :: Effect LocationState
currentLocationState = do
    win <- window
    loc <- Window.location win
    state <- Window.history win >>= History.state
    pathname <- Location.pathname loc
    search <- Location.search loc
    hash <- Location.hash loc
    let path = pathname <> search <> hash
    pure { state, pathname, search, hash, path }

readLocation :: PushStateInterface -> Effect LocationState
readLocation = _.locationState

pushUrl :: PushStateInterface -> String -> Effect Unit
pushUrl psi url = psi.pushState (unsafeToForeign unit) url

replaceUrl :: PushStateInterface -> String -> Effect Unit
replaceUrl psi url = psi.replaceState (unsafeToForeign unit) url

pushRoute :: PushStateInterface -> Route -> Effect Unit
pushRoute psi = pushUrl psi <<< routeUrl

replaceRoute :: PushStateInterface -> Route -> Effect Unit
replaceRoute psi = replaceUrl psi <<< routeUrl
